import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { AND as AndOperator } from '~/common/constants/openSearch';
import { Box } from '~/eds/src/components/Box/Box';
import { Button } from '~/eds/src/components/Button/Button';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Panel } from '~/eds/src/components/Panel/Panel';
import { useToggle } from '~/eds/src/hooks/useToggle';
import { Filter } from '~/evifields/src/types/Filter';
import { DragProvider } from '~/features/drag-and-drop/DragContext';
import { QueryOperatorEntity, SectionEntity } from '~/types/OpenSearch';

import { testIsSection } from '../utils/query';
import { FiltersAccordion } from './filters-accordion/FiltersAccordion';
import { OperatorSelect } from './operator-select/OperatorSelect';
import {
  isBooleanFilter,
  toSectionFiltersOperators,
} from './QueryBuilder.utils';
import { Section } from './section/Section';
import type { QueryBuilderProps, QueryGroup } from './types';
import { buildQueryWithQueryBuilder } from './utils';

const newOperator = { type: 'operator', value: 'and' } as QueryOperatorEntity;
const newSection = {
  type: 'section',
  value: { filters: [], operator: newOperator },
};

export const QueryBuilder = ({
  pinnedFiltersIds,
  searchFilters,
  query,
  onQueryChange,
  onHide,
}: QueryBuilderProps) => {
  const [isLeftPanelVisible, toggleHidden] = useToggle(true);
  const [sections, setSections] = useState<QueryGroup[]>([newSection]);
  const [operators, setOperators] = useState<QueryOperatorEntity[]>([]);
  const isAnySectionEmpty = sections.some(
    (section) => section.value.filters.length === 0,
  );
  const isAnyFilterEmpty = sections
    .flatMap((section) => section.value.filters)
    .some(
      (filter) =>
        !isBooleanFilter(filter.operatorId) && filter.values.length === 0,
    );

  const hasNoSections = sections.length === 0;

  const searchFiltersMap = useMemo(
    () =>
      Object.fromEntries(searchFilters.map((filter) => [filter.id, filter])),
    [searchFilters],
  );

  const handleAddSection = () => {
    setSections((prevSections) => {
      const newSections = [...prevSections, newSection];
      if (newSections.length > 1) {
        setOperators((prevOperators) => [...prevOperators, AndOperator]);
      }
      return newSections;
    });
  };

  const handleRemoveSection = (index: number) => {
    setSections((prevSections) => prevSections.filter((_, i) => i !== index));
    setOperators((prevOperators) =>
      prevOperators.filter((_, i) => i !== Math.max(0, index - 1)),
    );
  };

  const handleGroupOperatorSelect = (index: number, value: string) => {
    setOperators((prevOperators) =>
      prevOperators.map((op, i) =>
        i === index ? ({ type: 'operator', value } as QueryOperatorEntity) : op,
      ),
    );
  };

  const handleSectionOperatorSelect = (index: number, value: 'or' | 'and') => {
    setSections((prevSections) => {
      return prevSections.map((section, i) =>
        i === index
          ? {
              ...section,
              value: {
                ...section.value,
                operator: { type: 'operator', value },
              },
            }
          : section,
      );
    });
  };

  const handleAddFilter = (index: number, filter: Filter) => {
    setSections((prevSections) => {
      return prevSections.map((section, i) =>
        i === index
          ? {
              ...section,
              value: {
                ...section.value,
                filters: [...section.value.filters, filter],
              },
            }
          : section,
      );
    });
  };
  const handleRemoveFilter = (sectionIndex: number, filterIndex: number) => {
    setSections((prevSections) => {
      return prevSections.map((section, i) =>
        i === sectionIndex
          ? {
              ...section,
              value: {
                ...section.value,
                filters: section.value.filters.filter(
                  (_, j) => j !== filterIndex,
                ),
              },
            }
          : section,
      );
    });
  };

  const handleChangeFilter = (
    sectionIndex: number,
    filterIndex: number,
    newFilter: Filter,
  ) => {
    setSections((prevSections) => {
      return prevSections.map((section, i) =>
        i === sectionIndex
          ? {
              ...section,
              value: {
                ...section.value,
                filters: section.value.filters.map((filter, j) =>
                  j === filterIndex ? newFilter : filter,
                ),
              },
            }
          : section,
      );
    });
  };

  const handleSubmit = () => {
    const groupsEntity = sections.reduce(
      (acc: (QueryGroup | QueryOperatorEntity)[], val: QueryGroup, i) => {
        return i < sections.length - 1 && operators[i]
          ? acc.concat(val, operators[i])
          : acc.concat(val);
      },
      [],
    );

    const query = buildQueryWithQueryBuilder(groupsEntity, searchFiltersMap);

    onQueryChange(query);
  };

  const getOperator = (index: number) => {
    if (sections.length === 1 || index === sections.length - 1) return null;
    return (
      <OperatorSelect
        value={operators[index]?.value}
        onChange={(value) => handleGroupOperatorSelect(index, value)}
      />
    );
  };

  useEffect(() => {
    if (query && query.length && searchFiltersMap) {
      // If the query contains sections, it's already a complex query
      if (query.some(testIsSection)) {
        const querySections = (query as SectionEntity[]).filter(
          (item: SectionEntity) => item.type === 'section',
        );

        const queryGroupFilters = toSectionFiltersOperators(
          querySections,
          searchFiltersMap,
        );
        const operatorsArray = (query as QueryOperatorEntity[]).filter(
          (item: QueryOperatorEntity) => item.type === 'operator',
        );
        setSections(queryGroupFilters as QueryGroup[]);
        setOperators(operatorsArray);
      }
    }
  }, [query, searchFiltersMap]);

  const intl = useIntl();

  return (
    <DragProvider>
      <Panel
        hidden={{
          isHidden: false,
          onHide: onHide,
          placement: 'right',
        }}
        title={intl.formatMessage({
          id: 'QueryBuilder.advancedQueriesTitle',
          defaultMessage: 'Advanced Queries',
          description: 'Title for advanced queries panel',
        })}
        enableBackdrop
        position="right"
        mode="floating"
        width="720px"
        footer={{
          actions: [
            {
              text: intl.formatMessage({
                id: 'QueryBuilder.cancelButton',
                defaultMessage: 'Cancel',
                description: 'Cancel button text',
              }),
              onClick: onHide,
            },
            {
              text: intl.formatMessage({
                id: 'QueryBuilder.applyButton',
                defaultMessage: 'Apply',
                description: 'Apply button text',
              }),
              level: 'primary',
              disabled: isAnySectionEmpty || isAnyFilterEmpty || hasNoSections,
              onClick: handleSubmit,
            },
          ],
        }}
        sideContent={{
          content: (
            <FiltersAccordion
              pinnedFiltersIds={pinnedFiltersIds}
              searchFilters={searchFilters}
            />
          ),
          action: {
            text: intl.formatMessage({
              id: 'QueryBuilder.filtersToggle',
              defaultMessage: 'Filters',
              description: 'Text for filters toggle button',
            }),
            icon: 'left-panel',
            onClick: toggleHidden,
          },
          isVisible: isLeftPanelVisible,
        }}
      >
        <Layout mb={2} direction="column" spacing={4} align="center">
          {sections.map((section, index) => (
            <React.Fragment key={`section_${index}`}>
              <Box w="100%" px={4}>
                <Section
                  index={index}
                  searchFilters={searchFilters}
                  filters={section.value.filters}
                  operator={section.value?.operator?.value || 'and'}
                  onOperatorChange={(value) =>
                    handleSectionOperatorSelect(index, value)
                  }
                  onFilterAdd={(value) => handleAddFilter(index, value)}
                  onFilterChange={(filterIndex, filter) =>
                    handleChangeFilter(index, filterIndex, filter)
                  }
                  onFilterRemove={(filterIndex, sectionIndex) =>
                    handleRemoveFilter(sectionIndex ?? index, filterIndex)
                  }
                  onRemove={() => handleRemoveSection(index)}
                />
              </Box>

              {getOperator(index)}
            </React.Fragment>
          ))}
          <Button
            text={intl.formatMessage({
              id: 'QueryBuilder.addSectionButton',
              defaultMessage: 'Add Section',
              description: 'Button text to add a new section',
            })}
            variant="secondary-active"
            onClick={handleAddSection}
          />
        </Layout>
      </Panel>
    </DragProvider>
  );
};
